/**
 * WebAuthn / Passkeys Mobile Biometrics (Face ID, Touch ID, Fingerprint).
 *
 * PLAN v0.3: 모바일 기기 생체인식 암호 등록 및 간편 잠금해제 유틸리티.
 */

const STORAGE_PREFIX = 'talk2nick_bio_';

function bufferSourceToBytes(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function base64urlToBytes(str: string): Uint8Array {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** 현재 디바이스/브라우저가 생체인식(플랫폼 인증자)을 지원하는지 확인 */
export async function isBiometricsAvailable(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return false;
  try {
    if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
      return false;
    }
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** 특정 사용자의 생체인식 인증서가 디바이스에 등록되어 있는지 확인 */
export function hasRegisteredBiometrics(uid: string): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(localStorage.getItem(`${STORAGE_PREFIX}${uid}`));
}

/** 생체인식 인증서(Passkey/Credential) 등록 */
export async function registerBiometricCredential(
  uid: string,
  email: string,
): Promise<{ ok: boolean; error?: string }> {
  if (!(await isBiometricsAvailable())) {
    return { ok: false, error: 'biometrics-not-supported' };
  }

  const challenge = new Uint8Array(32);
  window.crypto.getRandomValues(challenge);

  try {
    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge: bufferSourceToBytes(challenge),
        rp: {
          name: 'talk2nIcK',
          id: window.location.hostname === 'localhost' ? 'localhost' : window.location.hostname,
        },
        user: {
          id: bufferSourceToBytes(new TextEncoder().encode(uid)),
          name: email,
          displayName: email,
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
        },
        timeout: 60_000,
      },
    })) as PublicKeyCredential | null;

    if (!credential) return { ok: false, error: 'registration-cancelled' };

    localStorage.setItem(`${STORAGE_PREFIX}${uid}`, credential.id);
    return { ok: true };
  } catch (error) {
    console.warn('[talk2nIcK] 생체인식 등록 실패:', error);
    return { ok: false, error: String(error) };
  }
}

/** 생체인식 인증 실행 (앱 잠금해제) */
export async function verifyBiometric(uid: string): Promise<boolean> {
  const credentialId =
    typeof window !== 'undefined' ? localStorage.getItem(`${STORAGE_PREFIX}${uid}`) : null;
  if (!credentialId) return false;

  const challenge = new Uint8Array(32);
  window.crypto.getRandomValues(challenge);

  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: bufferSourceToBytes(challenge),
        rpId: window.location.hostname === 'localhost' ? 'localhost' : window.location.hostname,
        allowCredentials: [
          {
            id: bufferSourceToBytes(base64urlToBytes(credentialId)),
            type: 'public-key',
          },
        ],
        userVerification: 'required',
        timeout: 60_000,
      },
    });

    return assertion !== null;
  } catch (error) {
    console.warn('[talk2nIcK] 생체인식 검증 실패/취소:', error);
    return false;
  }
}

/** 생체인식 등록 해제 */
export function clearBiometricCredential(uid: string): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(`${STORAGE_PREFIX}${uid}`);
  }
}
